import React, { useEffect, useState, useMemo, useCallback } from "react";
import useAuthStore from "../store/useAuthStore";
import { initMercadoPago, Payment, StatusScreen } from "@mercadopago/sdk-react";
import {
  FiShield,
  FiCreditCard,
  FiLoader,
  FiAlertCircle,
  FiLock,
  FiCheckCircle,
} from "react-icons/fi";
import api from "../utils/api";

const MP_PUBLIC_KEY = "APP_USR-20bebd3d-5d9e-4194-afcd-2b1abbcca6fd";

/**
 * Mapeo de tipos de documento para Mercado Pago
 */
const DOCUMENT_MAPPING = {
  CC: "CC",
  CE: "CE",
  NIT: "NIT",
  TI: "TI",
  PEP: "CE",
  PPT: "CE",
};

const getMpDocumentType = (docType) => DOCUMENT_MAPPING[docType] || "CC";

/**
 * MercadoPagoPayment
 * Componente refinado para el procesamiento de pagos.
 */
const MercadoPagoPayment = ({
  amount,
  preferenceId,
  payer = {},
  externalReference,
  onPaymentSuccess,
}) => {
  const [paymentId, setPaymentId] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const user = useAuthStore((state) => state.user);

  // Inicializar Mercado Pago al montar
  useEffect(() => {
    initMercadoPago(MP_PUBLIC_KEY, { locale: "es-CO" });
  }, []);

  // Configuración de inicialización memoizada
  const initialization = useMemo(() => {
    // Extraer datos de forma robusta
    const email = payer.email || user?.email || "";
    const firstName =
      payer.firstName ||
      payer.name ||
      payer.nombre ||
      user?.nombres ||
      user?.name ||
      "Cliente";
    const lastName =
      payer.lastName ||
      payer.last_name ||
      payer.apellidos ||
      user?.apellidos ||
      user?.last_name ||
      "";

    // Obtener tipo y número de documento
    const docType =
      payer.identification?.type ||
      payer.tipoDocumentoFacturacion ||
      payer.doc_type;
    const docNumber =
      payer.identification?.number ||
      payer.documentoFacturacion ||
      payer.objeto_identificador;

    return {
      amount,
      preferenceId,
      payer: {
        email,
        firstName,
        lastName,
        identification: {
          type: getMpDocumentType(docType),
          number: docNumber,
        },
      },
    };
  }, [amount, preferenceId, payer, user]);

  // Personalización visual memoizada
  const customization = useMemo(
    () => ({
      paymentMethods: {
        creditCard: "all",
        debitCard: "all",
        bankTransfer: "all",
      },
      visual: {
        style: {
          theme: "bootstrap", // Opciones elegantes: 'default', 'dark', 'bootstrap', 'flat'
        },
      },
    }),
    [],
  );

  // Manejador de envío del pago
  const handleSubmit = useCallback(
    async ({ selectedPaymentMethod, formData }) => {
      setIsSubmitting(true);
      setError(null);

      try {
        const dataToSend = {
          metadata: {
            externalReference,
            consultaId: externalReference,
          },
          paymentMethod: selectedPaymentMethod,
          formData,
        };

        const { data } = await api.post(
          "procesar-pago-antecedentes-mercado-pago",
          dataToSend,
        );

        if (data?.payment_id) {
          setPaymentId(data.payment_id);
          onPaymentSuccess(data.payment_id);
        } else {
          throw new Error("No se recibió la confirmación del pago.");
        }
      } catch (err) {
        console.error("Error al procesar el pago:", err);
        setError(
          err.response?.data?.message ||
            "No pudimos procesar tu pago. Por favor, verifica los datos e intenta de nuevo.",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [externalReference, onPaymentSuccess],
  );

  const handleError = useCallback((err) => {
    console.error("Error en MercadoPago Brick:", err);
    setError("Revisa los datos ingresados e intenta de nuevo.");
    setTimeout(() => {
      setError(null);
    }, 5000);
  }, []);

  const handleReady = useCallback(() => {
    setIsReady(true);
  }, []);

  // --- SUB-COMPONENTES DE ESTADO ---

  const ErrorView = () => (
    <div className="p-8 bg-white rounded-2xl border-2 border-red-50 flex flex-col items-center justify-center text-center shadow-xl animate-in fade-in slide-in-from-bottom-4">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
        <FiAlertCircle className="w-8 h-8 text-red-500" />
      </div>
      <h3 className="text-gray-900 font-bold text-lg mb-2">
        ¡Ups! Algo salió mal
      </h3>
      <p className="text-gray-500 text-sm max-w-xs mb-6">{error}</p>
      <button
        onClick={() => setError(null)}
        className="px-6 py-2.5 bg-gray-900 text-white rounded-xl font-semibold text-sm hover:bg-gray-800 transition-colors shadow-lg"
      >
        Reintentar
      </button>
    </div>
  );

  const LoadingOverlay = ({ message }) => (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm rounded-2xl animate-in fade-in duration-300">
      <div className="relative">
        <FiLoader className="w-12 h-12 text-emerald-600 animate-spin" />
        <div className="absolute inset-0 flex items-center justify-center animate-pulse">
          <FiLock className="w-4 h-4 text-emerald-700" />
        </div>
      </div>
      <p className="mt-4 text-gray-800 font-bold">{message}</p>
      <p className="text-gray-400 text-xs mt-1 italic">
        Protegiendo tu transacción...
      </p>
    </div>
  );

  // --- RENDERIZADO PRINCIPAL ---

  if (error && !isReady) return <ErrorView />;

  if (paymentId) {
    return (
      <div className="w-full animate-in fade-in zoom-in-95 duration-500">
        <StatusScreen
          initialization={{ paymentId }}
          onReady={() => console.log("Status Screen listo")}
          onError={handleError}
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Container Principal */}
      <div className="relative bg-white rounded-2xl border border-gray-100/50 p-1">
        {/* Header de la Pasarela */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 gap-4 bg-gradient-to-br from-gray-50 to-white rounded-t-xl border-b border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 shadow-inner shrink-0">
              <FiShield size={24} />
            </div>
            <div>
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-wider">
                Pago Seguro
              </h2>
              <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                <FiLock size={12} className="text-emerald-500" />
                Encriptado SSL de 256 bits
              </div>
            </div>
          </div>

          <div className="sm:text-right w-full sm:w-auto pt-4 sm:pt-0 border-t sm:border-t-0 border-gray-100">
            <span className="block text-[10px] uppercase font-bold text-gray-400 tracking-tighter">
              Monto a pagar
            </span>
            <span className="text-2xl font-black text-emerald-700 leading-none">
              ${amount?.toLocaleString("es-CO")}
            </span>
          </div>
        </div>

        {/* Contenido del Brick */}
        <div className="p-4 relative min-h-[400px]">
          {/* Overlays de estado */}
          {(!isReady || isSubmitting) && (
            <LoadingOverlay
              message={
                isSubmitting
                  ? "Procesando pago..."
                  : "Cargando opciones de pago..."
              }
            />
          )}

          {/* Formulario de Mercado Pago */}
          <div
            className={`transition-all duration-700 ${isReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            <Payment
              initialization={initialization}
              customization={customization}
              onSubmit={handleSubmit}
              onReady={handleReady}
              onError={handleError}
            />
          </div>

          {/* Errores de envío post-brick */}
          {error && isReady && (
            <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
              <FiAlertCircle className="text-red-500 mt-0.5 shrink-0" />
              <div className="text-xs text-red-700">
                <p className="font-bold mb-0.5">Atención</p>
                <p>{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Institucional */}
        <div className="px-6 py-4 bg-gray-50/50 rounded-b-xl border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-gray-400">
            <div className="flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase opacity-60">
              <FiCreditCard size={14} />
              Crédito / Débito
            </div>
            <div className="w-[1px] h-3 bg-gray-300" />
            <div className="text-[10px] font-bold tracking-widest uppercase opacity-60">
              PSE / Efecty
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <FiCheckCircle size={14} className="text-emerald-500" />
            <span className="text-[10px] font-bold text-gray-500">
              PROTECCIÓN AL COMPRADOR
            </span>
          </div>
        </div>
      </div>

      {/* Sellos de Confianza Externos */}
      <div className="mt-6 flex flex-wrap justify-center items-center gap-8 opacity-40 grayscale pointer-events-none">
        {/* VISA - Usando un SVG de un CDN más estable */}
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/d/d6/Visa_2021.svg"
          alt="Visa"
          className="h-4 w-auto"
          onError={(e) => {
            e.target.src = "https://cdn.worldvectorlogo.com/logos/visa-10.svg";
          }}
        />

        {/* MASTERCARD */}
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg"
          alt="Mastercard"
          className="h-7 w-auto"
        />

        {/* AMERICAN EXPRESS */}
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/f/fa/American_Express_logo_%282018%29.svg"
          alt="American Express"
          className="h-7 w-auto"
        />

        {/* PSE - Versión PNG de alta calidad */}
        <img
          src="https://images.seeklogo.com/logo-png/43/1/pse-logo-png_seeklogo-433463.png"
          alt="PSE"
          className="h-9 w-auto"
        />
      </div>
    </div>
  );
};

export default MercadoPagoPayment;
